import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import { exceedsMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath, ExtraditionMlaWizardShell, FieldError, inpClass } from './ExtraditionMlaWizardShell';
import { useExtraditionMlaLookups } from './useExtraditionMlaLookups';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function ExtraditionMlaStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useExtraditionMlaLookups('step1', draft.provinceId);

  const countryOptions = useMemo(
    () =>
      toOpts(
        mergeLookupItems(lookups.countries, draft.countryInvolvedId, draft.countryInvolvedName),
      ),
    [draft.countryInvolvedId, draft.countryInvolvedName, lookups.countries],
  );
  const provinceOptions = useMemo(
    () => toOpts(mergeLookupItems(lookups.provinces, draft.provinceId, draft.provinceName)),
    [draft.provinceId, draft.provinceName, lookups.provinces],
  );
  const districtOptions = useMemo(
    () => toOpts(mergeLookupItems(lookups.districts, draft.districtId, draft.districtName)),
    [draft.districtId, draft.districtName, lookups.districts],
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.hasConnectedMatters === 'yes' || draft.hasConnectedMatters === 'no') return;
    setDraft((d) => ({ ...d, hasConnectedMatters: 'no' }));
  }, [draft.hasConnectedMatters, setDraft]);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(1, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(extraditionMlaWizardPath(2, draft.draftRequestId));
  };

  return (
    <ExtraditionMlaWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="emla-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}            value={draft.description}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.caseSource} <span className="req">*</span>
          </label>
          <textarea
            id="emla-caseSource"
            className={inpClass(Boolean(errors.caseSource))}
            rows={3}            value={draft.caseSource}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({ ...d, caseSource: maxLen('caseSource', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.sourceOfCase}
          />
          <FieldError message={errors.caseSource} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.countryInvolved} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-countryInvolved"
              hasError={Boolean(errors.countryInvolvedId)}
              value={draft.countryInvolvedId}
              disabled={lookups.loading}
              placeholder={lookups.loading ? f.loadingCountries : f.selectCountry}
              options={countryOptions}
              onChange={(id) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  countryInvolvedId: id,
                  countryInvolvedName: lookupName(lookups.countries, id) }));
                clearErr('countryInvolvedId');
              }}
            />
            <FieldError message={errors.countryInvolvedId} />
          </div>
          <div className="ccr-fg">
            <label>{f.directionOfRequest}</label>
            <input
              id="emla-direction"
              className={inpClass(Boolean(errors.directionOfRequest))}
              type="text"              value={draft.directionOfRequest}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  directionOfRequest: maxLen('directionOfRequest', e.target.value, MAX_REF) }));
              }}
              placeholder={f.incomingOutgoing}
            />
            <FieldError message={errors.directionOfRequest} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              disabled={lookups.loading}
              placeholder={f.selectProvince}
              options={provinceOptions}
              onChange={(id) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.district} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-district"
              hasError={Boolean(errors.districtId)}
              value={draft.districtId}
              disabled={!draft.provinceId || lookups.loading}
              placeholder={f.selectDistrict}
              options={districtOptions}
              onChange={(id) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id) }));
                clearErr('districtId');
              }}
            />
            <FieldError message={errors.districtId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="emla-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.policeMagistrateRef}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              id="emla-otherRef"
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"              value={draft.otherExternalReferences}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }));
              }}
              placeholder={f.commaSeparatedReferences}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              id="emla-caseNumber"
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"              value={draft.caseNumber}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.connectedMatters} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row" id="emla-connected">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="emla-connected"
                  checked={draft.hasConnectedMatters === v}
                  onChange={() => {
                    setDraft((d: ExtraditionMlaWizardDraft) => ({
                      ...d,
                      hasConnectedMatters: v,
                      ...(v === 'no'
                        ? {
                            connectedCaseNumber: '',
                            connectedAgdFileRefAvailable: false,
                            connectedAgdFileRef: '' }
                        : {
                            connectedAgdFileRefAvailable: false,
                            connectedAgdFileRef: '' }) }));
                    clearErr('hasConnectedMatters');
                    clearErr('connectedAgdFileRef');
                  }}
                />
                {v === 'yes' ? f.yes : f.no}
              </label>
            ))}
          </div>
          <FieldError message={errors.hasConnectedMatters} />
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg">
              <label>
                {f.connectedCaseNumber} <span className="req">*</span>
              </label>
              <input
                id="emla-connectedCase"
                className={inpClass(Boolean(errors.connectedCaseNumber))}
                type="text"                value={draft.connectedCaseNumber}
                onChange={(e) => {
                  setDraft((d: ExtraditionMlaWizardDraft) => ({
                    ...d,
                    connectedCaseNumber: maxLen('connectedCaseNumber', e.target.value, MAX_REF) }));
                }}
              />
              <FieldError message={errors.connectedCaseNumber} />
            </div>
            <div className="ccr-fg">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 6 }}
              >
                <label style={{ marginBottom: 0 }}>
                  {f.connectedAgdFileRef}
                  {draft.connectedAgdFileRefAvailable ? <span className="req"> *</span> : null}
                </label>
                <label className="ccr-check" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                  <input
                    id="emla-connectedAgdAvailable"
                    type="checkbox"
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d: ExtraditionMlaWizardDraft) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '' }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>{f.available}</span>
                </label>
              </div>
              <input
                id="emla-connectedAgd"
                className={inpClass(Boolean(errors.connectedAgdFileRef))}
                type="text"                disabled={!draft.connectedAgdFileRefAvailable}
                value={draft.connectedAgdFileRef}
                onChange={(e) => {
                  setDraft((d: ExtraditionMlaWizardDraft) => ({
                    ...d,
                    connectedAgdFileRef: maxLen('connectedAgdFileRef', e.target.value, MAX_REF) }));
                }}
              />
              <p className="up-hint" style={{ marginTop: 6 }}>
                {f.connectedAgdHint}
              </p>
              <FieldError message={errors.connectedAgdFileRef} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
